import { useRoute, useRouter } from 'vue-router'
import type { LocationQuery, RouteLocationNormalizedLoaded } from 'vue-router'

type QueryParams = Record<string, string>

/**
 * Composable for reactive URL query parameter management.
 * Supports direct updates (`setQuery`) and functional-style updates (`customUpdateQuery`).
 */
export const useQueryParams = () => {
  const route = useRoute() as RouteLocationNormalizedLoaded
  const router = useRouter()

  // // --- Core Update Function (private) ---
  // const _updateUrl = (newQuery: QueryParams) => {
  //   const searchParams = new URLSearchParams()
  //   Object.entries(newQuery).forEach(([key, value]) => {
  //     if (value) searchParams.append(key, value)
  //   })
  //   const newUrl = searchParams.toString()
  //     ? `${window.location.pathname}?${searchParams.toString()}`
  //     : window.location.pathname
  //   window.history.replaceState(null, '', newUrl)
  // }

  // --- Core Update Function ---
  const _updateUrl = async (newQuery: LocationQuery) => {
    await router.replace({
      query: {
        ...Object.fromEntries(
          Object.entries(newQuery)
            .filter(([_, value]) => (
              value !== undefined &&
              value !== null &&
              value !== '' &&
              !(Array.isArray(value) && value.length === 0)
            ))
        )
      }
    })
  }

  // --- Public Methods ---

  /**
   * Functional-style update (original design).
   * @param updater - Callback that receives the current query and modifies it.
   */
  const customUpdateQuery = async (updater: (query: QueryParams) => void) => {
    const newQuery = { ...route.query } as QueryParams
    updater(newQuery) // Let the caller modify the query
    await _updateUrl(newQuery)
  }

  /**
   * Sets a query parameter (convenience method).
   * @param key - Query key
   * @param value - Query value (empty/undefined/null string removes the key)
   */
  const setQuery = async (key: string, value: string | undefined | null) => {
    await customUpdateQuery((query) => {
      if (value) query[key] = value
      else delete query[key] // Remove if value is falsy
    })
  }

  return {
    customUpdateQuery, // Functional style
    setQuery,         // Simple key-value setter
    currentQuery: route.query, // Reactive access
  }
}
